import { useEffect, useState } from "react";
import { authFetch } from "../utils/api/authFetch";
import { sortClasses } from "../utils/classOrder";

// The subjects and classes a teacher can tag content with are scoped to their
// own "What you teach" profile (Marketplace → Profile, stored on
// marketplace_plans.subjects/.classes), NOT the whole question bank. Topics stay
// global-per-subject (/api/quiz/metadata → topicsBySubject) and are shared
// across every class, so a teacher reuses one topic set no matter which class a
// lesson targets.
//
// Shared by the three lesson-authoring surfaces (teacher_content,
// teacher_content_build, teacher_marketplace/StudioContent) so they stay
// consistent. `needsSetup` is true when the teacher hasn't finished their
// profile — the pages use it to steer them to Marketplace → Profile instead of
// letting them tag a lesson with a subject/class they don't teach.
export const useTeacherTaxonomy = () => {
    const [state, setState] = useState({
        subjects: [],        // teacher's profile subjects (canonical strings)
        classes: [],         // teacher's profile classes (e.g. "SS2")
        topicsBySubject: {}, // global, keyed by subject; shared across classes
        plan: null,          // raw plan.data (so callers can read price, etc.)
        loading: true,
    });

    const reload = async () => {
        setState((s) => ({ ...s, loading: true }));
        try {
            const [pRes, mRes] = await Promise.all([
                authFetch("/api/marketplace/plan"),
                authFetch("/api/quiz/metadata"),
            ]);
            const p = await pRes.json().catch(() => ({}));
            const m = await mRes.json().catch(() => ({}));
            const subjects = Array.isArray(p.data?.subjects) ? p.data.subjects : [];
            const classes = Array.isArray(p.data?.classes) ? p.data.classes : [];
            setState({
                subjects: [...subjects].sort((a, b) => a.localeCompare(b)),
                // The profile stores classes in whatever order the teacher tapped
                // them; always present them in school order (JSS1 → SS3).
                classes: sortClasses(classes),
                topicsBySubject: m.topicsBySubject || {},
                plan: p.data || null,
                loading: false,
            });
        } catch {
            setState((s) => ({ ...s, loading: false }));
        }
    };

    useEffect(() => { reload(); }, []);

    const hasSubjects = state.subjects.length > 0;
    const hasClasses = state.classes.length > 0;
    return {
        ...state,
        hasSubjects,
        hasClasses,
        // A teacher needs BOTH a subject and a class to tag a lesson.
        needsSetup: !state.loading && (!hasSubjects || !hasClasses),
        reload,
    };
};
